/**
 * ActivityLogPage — full operational activity log with filters, stats, and export.
 */

import React, { useState, useEffect, useRef, useMemo } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { DeviceIDBadge, ComplaintIDBadge, SeverityBadge } from '../../shared/components/common/Badges';
import StatsCard from '../../shared/components/common/StatsCard';
import MoreFiltersButton from '../../shared/components/common/MoreFiltersButton';
import { FilterTitleHint } from '../../shared/components/common/advancedFilterDrawerHints';
import Pagination from '../../shared/components/common/Pagination';
import ExportButton from '../../shared/components/modals/ExportButton';
import ExportColumnConfigurationModal from '../../shared/components/modals/ExportColumnConfigurationModal';
import ComplaintView from '../../features/complaints/components/ComplaintView';
import MaintenanceLogView from '../../features/devices/components/MaintenanceLogView';
import { API_ENDPOINTS } from '../../config/apiConfig';
import {
  ACTIVITY_LOG_DEFAULT_LOOKBACK_DAYS,
  DATE_PRESETS,
  createInitialActivityLogFilters,
  buildActivityLogApiParams,
  getActivityTypeOptionsForRole,
  getActivityTypeLabel,
  getCategoryPresetsForRole,
  categoryToActivityTypes,
  isLocationScopedRole,
  daysAgoDateString,
  MAINTENANCE_TYPE_OPTIONS,
  SEVERITY_OPTIONS,
  COMPLAINT_STATUS_OPTIONS,
  ACTIVITY_LOG_FILTER_HINTS,
  getActivityLogDateFilterLabel,
  getActivityLogCategoryFilterLabel,
  hasActivityLogActiveFilters,
  isActivityLogServerDateWindowActive,
  isActivityLogLastNDaysActive,
  hasComplaintAdvancedFilters,
  hasMaintenanceAdvancedFilters,
  isDeviceOnlyActivitySelection,
  compatibleActivityTypeFilters,
  isActivityTypeOptionDisabled,
} from '../../features/activity-log/constants/activityLogFilters';
import {
  createActivityLogExportColumnsState,
  getSelectedExportColumnHeaders,
} from '../../features/activity-log/constants/activityLogExportColumns';
import {
  getActivityDescriptionParts,
  resolveDepartmentActivitySummary,
} from '../../features/dashboard/pages/recentActivityHelpers';
import { exportFilteredActivityLog } from '../../utils/exportUtils';
import { getListPageSize, saveUiPreferencePatch } from '../../shared/utils/uiPreferences';
import '../../features/devices/components/DeviceMaintenanceHistory.css';
import '../../features/dashboard/pages/Dashboards.css';
import './ActivityLogPage.css';

const ACTIVITY_LOG_PRESET_KEY = 'activityLogPreset';

const getMaintenanceTypeLabel = (maintenanceType) => {
  const labelMap = { ppm: 'PPM', calibration: 'CAL', compliance: 'COM' };
  return labelMap[maintenanceType?.toLowerCase()] || maintenanceType;
};

const formatActivityDateTime = (timestamp) => {
  const d = new Date(timestamp);
  if (Number.isNaN(d.getTime())) return { date: '—', time: '' };
  return {
    date: d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
    time: d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }),
  };
};

const formatActivityTime = (timestamp) => {
  const { date, time } = formatActivityDateTime(timestamp);
  return time ? `${date} ${time}` : date;
};

const readInitialPresetDays = () => {
  const preset = sessionStorage.getItem(ACTIVITY_LOG_PRESET_KEY);
  sessionStorage.removeItem(ACTIVITY_LOG_PRESET_KEY);
  if (preset === '7d') return 7;
  if (preset === '15d') return 15;
  if (preset === '30d') return 30;
  if (preset === '90d') return 90;
  return null;
};

const ActivityLogPage = ({
  token,
  user,
  onNavigateToDeviceView,
  onNavigateToMaintenanceHistory,
}) => {
  const role = user?.role;
  const locationScoped = isLocationScopedRole(role);
  const initialPresetDays = useMemo(() => readInitialPresetDays(), []);

  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [scopeLabel, setScopeLabel] = useState('');
  const [summary, setSummary] = useState(null);

  const [filters, setFilters] = useState(() => createInitialActivityLogFilters(initialPresetDays));
  const [drawerFilters, setDrawerFilters] = useState(() => createInitialActivityLogFilters(initialPresetDays));
  const [showAdvancedPanel, setShowAdvancedPanel] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    dateRange: true,
    activityTypes: true,
    department: true,
    maintenanceType: true,
    complaintFilters: true,
  });
  const [availableDepartments, setAvailableDepartments] = useState([]);

  const [searchInputValue, setSearchInputValue] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const searchDebounceTimerRef = useRef(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(() => getListPageSize());
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [sorting, setSorting] = useState({ sort_by: 'timestamp', sort_direction: 'desc' });

  const [selectedComplaintForModal, setSelectedComplaintForModal] = useState(null);
  const [selectedMaintenanceForModal, setSelectedMaintenanceForModal] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  const [showExportColumnsConfig, setShowExportColumnsConfig] = useState(false);
  const [exportColumns, setExportColumns] = useState(() => createActivityLogExportColumnsState());

  const activityTypeOptions = useMemo(() => getActivityTypeOptionsForRole(role), [role]);
  const categoryPresets = useMemo(() => getCategoryPresetsForRole(role), [role]);
  const stats = resolveDepartmentActivitySummary(summary, activities);
  const drawerHasComplaintFilters = hasComplaintAdvancedFilters(drawerFilters);
  const drawerHasMaintenanceFilters = hasMaintenanceAdvancedFilters(drawerFilters);
  const drawerDeviceOnly = isDeviceOnlyActivitySelection(drawerFilters.activityTypeFilters);
  const maintenanceTypeDisabled = drawerHasComplaintFilters || drawerDeviceOnly;
  const complaintFiltersDisabled = drawerHasMaintenanceFilters || drawerDeviceOnly;

  const buildFetchParams = () => {
    const params = buildActivityLogApiParams({
      filters,
      searchTerm,
      page: currentPage,
      perPage,
      sorting,
    });
    if (filters.categoryPreset && filters.categoryPreset !== 'all' && !params.activity_types) {
      const types = categoryToActivityTypes(filters.categoryPreset, role);
      if (types.length > 0) {
        params.activity_types = types.join(',');
      }
    }
    return params;
  };

  useEffect(() => {
    fetchActivities();
  }, [token, currentPage, perPage, searchTerm, sorting, filters]);

  useEffect(() => {
    if (searchDebounceTimerRef.current) {
      clearTimeout(searchDebounceTimerRef.current);
    }
    if (searchInputValue === '') {
      setSearchTerm('');
      setCurrentPage(1);
      return undefined;
    }
    searchDebounceTimerRef.current = setTimeout(() => {
      setSearchTerm(searchInputValue);
      setCurrentPage(1);
    }, 500);
    return () => {
      if (searchDebounceTimerRef.current) {
        clearTimeout(searchDebounceTimerRef.current);
      }
    };
  }, [searchInputValue]);

  useEffect(() => {
    if (!locationScoped || !token) return undefined;
    let cancelled = false;
    axios.get(API_ENDPOINTS.DEPARTMENTS.LIST, {
      headers: { Authorization: `Bearer ${token}` },
    }).then((response) => {
      if (!cancelled) {
        setAvailableDepartments(response.data.departments || []);
      }
    }).catch(() => {
      if (!cancelled) setAvailableDepartments([]);
    });
    return () => { cancelled = true; };
  }, [token, locationScoped]);

  const fetchActivities = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(API_ENDPOINTS.ACTIVITY_LOG.LIST, {
        params: buildFetchParams(),
        headers: { Authorization: `Bearer ${token}` },
      });
      setActivities(response.data.activities || []);
      setSummary(response.data.summary || null);
      setScopeLabel(response.data.scope_label || '');
      setTotalRecords(response.data.total_count || 0);
      setTotalPages(response.data.total_pages || 0);
    } catch (err) {
      console.error('Error fetching activity log:', err);
      setError('Failed to load activity log');
      toast.error('Failed to load activity log');
    } finally {
      setLoading(false);
    }
  };

  const applyDatePreset = (preset) => {
    setCurrentPage(1);
    setFilters((prev) => {
      if (isActivityLogLastNDaysActive(prev, preset.days)) {
        return { ...prev, dateFrom: '', dateTo: '', datePreset: '' };
      }
      return {
        ...prev,
        dateFrom: daysAgoDateString(preset.days),
        dateTo: '',
        datePreset: preset.id,
      };
    });
  };

  const applyCategoryPreset = (presetId) => {
    setCurrentPage(1);
    setFilters((prev) => ({
      ...prev,
      categoryPreset: prev.categoryPreset === presetId ? 'all' : presetId,
      activityTypeFilters: [],
    }));
  };

  const applyFilters = (next) => {
    setFilters(next);
    setCurrentPage(1);
  };

  const resetDateFilter = () => {
    applyFilters({
      ...filters,
      dateFrom: '',
      dateTo: '',
      datePreset: '',
    });
  };

  const resetCategoryFilter = () => {
    applyFilters({
      ...filters,
      categoryPreset: 'all',
      activityTypeFilters: [],
    });
  };

  const hasAdditionalFilters = () => {
    const explicitCustomDate = (filters.dateFrom || filters.dateTo)
      && !DATE_PRESETS.some((preset) => isActivityLogLastNDaysActive(filters, preset.days));
    return (
      filters.activityTypeFilters.length > 0
      || filters.maintenanceTypeFilters.length > 0
      || filters.complaintStatusFilters.length > 0
      || filters.severityFilters.length > 0
      || filters.departmentFilters.length > 0
      || explicitCustomDate
    );
  };

  const isAllQuickFilterActive = !hasActivityLogActiveFilters(filters);

  const clearAllFilters = () => {
    const fresh = createInitialActivityLogFilters();
    setFilters(fresh);
    setDrawerFilters(fresh);
    setShowAdvancedPanel(false);
    setCurrentPage(1);
  };

  const applyDrawerFilters = () => {
    if (hasMaintenanceAdvancedFilters(drawerFilters) && hasComplaintAdvancedFilters(drawerFilters)) {
      toast.error('Cannot apply both Maintenance Type and Complaint filters. Choose only one group.');
      return;
    }
    const next = {
      ...drawerFilters,
      categoryPreset: 'all',
      activityTypeFilters: compatibleActivityTypeFilters(
        drawerFilters,
        drawerFilters.activityTypeFilters,
      ),
    };
    if (hasMaintenanceAdvancedFilters(next)) {
      next.complaintStatusFilters = [];
      next.severityFilters = [];
    }
    if (hasComplaintAdvancedFilters(next)) {
      next.maintenanceTypeFilters = [];
    }
    const matchingDatePreset = DATE_PRESETS.find(
      (preset) => isActivityLogLastNDaysActive(next, preset.days),
    );
    next.datePreset = matchingDatePreset ? matchingDatePreset.id : '';
    setFilters(next);
    setShowAdvancedPanel(false);
    setCurrentPage(1);
  };

  const openAdvancedPanel = () => {
    const next = { ...filters };
    if (next.categoryPreset && next.categoryPreset !== 'all' && next.activityTypeFilters.length === 0) {
      next.activityTypeFilters = categoryToActivityTypes(next.categoryPreset, role);
    }
    setDrawerFilters(next);
    setShowAdvancedPanel(true);
  };

  const toggleSection = (key) => {
    setExpandedSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleExport = async (format) => {
    if (format !== 'excel') {
      toast.info('Activity log export is available in Excel format');
      return;
    }
    try {
      setIsExporting(true);
      const columns = getSelectedExportColumnHeaders(exportColumns);
      const params = buildFetchParams();
      delete params.page;
      delete params.limit;
      await exportFilteredActivityLog(params, 'excel', token, columns);
      toast.success('Activity log exported');
    } catch (err) {
      toast.error(err.message || 'Export failed');
    } finally {
      setIsExporting(false);
    }
  };

  const openComplaint = (act) => {
    if (!act?.complaint_id) return;
    setSelectedComplaintForModal({ id: act.complaint_id });
  };

  const openDevice = (act) => {
    if (!act?.device_id || typeof onNavigateToDeviceView !== 'function') return;
    onNavigateToDeviceView(act.device_id);
  };

  const openMaintenance = (act) => {
    if (act?.maintenance_log_id) {
      setSelectedMaintenanceForModal({ id: act.maintenance_log_id });
      return;
    }
    const deviceIdentifier = act?.device_identifier || act?.entity_id;
    if (deviceIdentifier && typeof onNavigateToMaintenanceHistory === 'function') {
      onNavigateToMaintenanceHistory({ device_identifier: deviceIdentifier });
    }
  };

  const handleSortChange = (column) => {
    setCurrentPage(1);
    setSorting((prev) => {
      if (prev.sort_by === column) {
        return {
          ...prev,
          sort_direction: prev.sort_direction === 'asc' ? 'desc' : 'asc',
        };
      }
      return { sort_by: column, sort_direction: column === 'timestamp' ? 'desc' : 'asc' };
    });
  };

  const getSortIndicator = (column) => {
    if (sorting.sort_by !== column) return '';
    return sorting.sort_direction === 'asc' ? ' ▲' : ' ▼';
  };

  const isServerDateWindowActive = isActivityLogServerDateWindowActive(filters);
  const categoryQuickFilters = categoryPresets.filter((preset) => preset.id !== 'all');

  return (
    <div className="work-logs-container activity-log-page">
      <div className="work-logs-description-section">
        <div className="title-with-button">
          <h2 className="work-logs-title">📋 Activity Log</h2>
          <button
            type="button"
            onClick={fetchActivities}
            className="btn-refresh"
            title="Refresh activity log"
            disabled={loading}
            style={{ padding: '0.5rem', fontSize: '1.2rem', border: 'none', background: 'none', cursor: 'pointer', lineHeight: 1 }}
          >
            🔄
          </button>
        </div>
        <p className="work-logs-subtitle">
          {scopeLabel
            ? `${scopeLabel}. Events in your scope — opened, closed, logged, and devices added. Work records are in Maintenance History.`
            : 'Events in your scope — opened, closed, logged, and devices added. Work records are in Maintenance History.'}
        </p>
      </div>

      {isServerDateWindowActive && (
        <div className="list-default-window-note" role="note">
          <span className="list-default-window-note__icon" aria-hidden="true">ℹ️</span>
          <span>
            Showing activity from the{' '}
            <strong>last {ACTIVITY_LOG_DEFAULT_LOOKBACK_DAYS} days</strong> by default.
            Use Last 7d / 15d / 30d or Advanced Filters for a different range.
          </span>
        </div>
      )}

      <div className="work-logs-header">
        <div className="dashboard-stats-summary activity-log-stats">
        <StatsCard icon="📋" number={stats.complaints_opened} label="Complaints Opened" className="open" />
        <StatsCard icon="✅" number={stats.complaints_closed} label="Complaints Closed" className="closed" />
        <StatsCard icon="❌" number={stats.complaints_cancelled} label="Complaints Cancelled" className="cancelled" />
        {locationScoped && (
          <StatsCard icon="⬆️" number={stats.complaints_escalated || 0} label="Escalated" className="escalated" />
        )}
        <StatsCard icon="🔧" number={stats.maintenance_by_type?.ppm || 0} label="PPM" className="pending" />
        <StatsCard icon="⚙️" number={stats.maintenance_by_type?.calibration || 0} label="Calibration" className="calibration" />
        <StatsCard icon="📋" number={stats.maintenance_by_type?.compliance || 0} label="Compliance" className="active" />
        <StatsCard icon="➕" number={stats.devices_added || 0} label="Devices Added" className="added" />
        </div>
      </div>

      <div className="search-section">
        <div className="search-box-wrapper">
          <span className="search-icon-lens" aria-hidden="true">🔍</span>
          <input
            type="text"
            className="search-input-clean"
            placeholder="Search device ID, complaint ID, description, actor..."
            value={searchInputValue}
            onChange={(e) => setSearchInputValue(e.target.value)}
          />
          {searchInputValue && (
            <button
              type="button"
              className="clear-search-btn-inline"
              onClick={() => setSearchInputValue('')}
              aria-label="Clear search"
            >
              ×
            </button>
          )}
        </div>
      </div>

      <div className="quick-filters">
        <span className="quick-filters-title">Quick Filters</span>
        <div className="preset-buttons">
          <button
            type="button"
            className={`preset-btn${isAllQuickFilterActive ? ' active' : ''}`}
            onClick={clearAllFilters}
            title={`Clear quick filters (default last ${ACTIVITY_LOG_DEFAULT_LOOKBACK_DAYS} days still applies)`}
          >
            All
          </button>
          {DATE_PRESETS.map((preset) => (
            <button
              key={preset.id}
              type="button"
              className={`preset-btn${isActivityLogLastNDaysActive(filters, preset.days) ? ' active' : ''}`}
              onClick={() => applyDatePreset(preset)}
            >
              {preset.label}
            </button>
          ))}
          {categoryQuickFilters.map((preset) => (
            <button
              key={preset.id}
              type="button"
              className={`preset-btn${filters.categoryPreset === preset.id ? ' active' : ''}`}
              onClick={() => applyCategoryPreset(preset.id)}
            >
              {preset.label}
            </button>
          ))}
          <MoreFiltersButton
            isOpen={showAdvancedPanel}
            hasAdditional={hasAdditionalFilters()}
            onClick={() => {
              if (showAdvancedPanel) {
                setShowAdvancedPanel(false);
              } else {
                openAdvancedPanel();
              }
            }}
          />
        </div>
      </div>

      {hasActivityLogActiveFilters(filters) && (
        <div className="active-filters">
          <div className="active-filters-header">
            <span className="active-filters-label">Filters Active:</span>
            <button type="button" className="clear-all-btn" onClick={clearAllFilters}>
              Clear All
            </button>
          </div>
          <div className="active-filters-pills">
            {(() => {
              const pills = [];

              const pushWithAnd = (node) => {
                if (pills.length > 0) {
                  pills.push(
                    <span key={`and-${pills.length}`} className="filter-and-connector"> AND </span>
                  );
                }
                pills.push(node);
              };

              const dateLabel = getActivityLogDateFilterLabel(filters);
              if (dateLabel) {
                pushWithAnd(
                  <span key="date-pill" className="filter-pill">
                    {dateLabel}
                    <button
                      type="button"
                      className="filter-pill-remove"
                      onClick={resetDateFilter}
                      aria-label="Reset date filter"
                    >
                      ✕
                    </button>
                  </span>
                );
              }

              const categoryLabel = getActivityLogCategoryFilterLabel(filters, role);
              if (categoryLabel) {
                pushWithAnd(
                  <span key="category-pill" className="filter-pill">
                    {categoryLabel}
                    <button
                      type="button"
                      className="filter-pill-remove"
                      onClick={resetCategoryFilter}
                      aria-label="Reset activity category filter"
                    >
                      ✕
                    </button>
                  </span>
                );
              }

              filters.activityTypeFilters.forEach((typeId) => {
                const opt = activityTypeOptions.find((o) => o.id === typeId);
                pushWithAnd(
                  <span key={typeId} className="filter-pill">
                    {opt?.label || typeId}
                    <button
                      type="button"
                      className="filter-pill-remove"
                      onClick={() => applyFilters({
                        ...filters,
                        activityTypeFilters: filters.activityTypeFilters.filter((id) => id !== typeId),
                      })}
                      aria-label={`Remove ${opt?.label || typeId} filter`}
                    >
                      ✕
                    </button>
                  </span>
                );
              });

              (filters.complaintStatusFilters || []).forEach((status) => {
                const opt = COMPLAINT_STATUS_OPTIONS.find((o) => o.id === status);
                pushWithAnd(
                  <span key={`status-${status}`} className="filter-pill">
                    Status: {opt?.label || status}
                    <button
                      type="button"
                      className="filter-pill-remove"
                      onClick={() => applyFilters({
                        ...filters,
                        complaintStatusFilters: filters.complaintStatusFilters.filter((id) => id !== status),
                      })}
                      aria-label={`Remove ${status} status filter`}
                    >
                      ✕
                    </button>
                  </span>
                );
              });

              filters.maintenanceTypeFilters.forEach((typeId) => {
                const opt = MAINTENANCE_TYPE_OPTIONS.find((o) => o.id === typeId);
                pushWithAnd(
                  <span key={`maint-${typeId}`} className="filter-pill">
                    Maintenance: {opt?.label || typeId}
                    <button
                      type="button"
                      className="filter-pill-remove"
                      onClick={() => applyFilters({
                        ...filters,
                        maintenanceTypeFilters: filters.maintenanceTypeFilters.filter((id) => id !== typeId),
                      })}
                      aria-label={`Remove ${opt?.label || typeId} maintenance filter`}
                    >
                      ✕
                    </button>
                  </span>
                );
              });

              filters.severityFilters.forEach((severity) => {
                const opt = SEVERITY_OPTIONS.find((o) => o.id === severity);
                pushWithAnd(
                  <span key={`sev-${severity}`} className="filter-pill">
                    Severity: {opt?.label || severity}
                    <button
                      type="button"
                      className="filter-pill-remove"
                      onClick={() => applyFilters({
                        ...filters,
                        severityFilters: filters.severityFilters.filter((id) => id !== severity),
                      })}
                      aria-label={`Remove ${severity} severity filter`}
                    >
                      ✕
                    </button>
                  </span>
                );
              });

              filters.departmentFilters.forEach((dept) => {
                pushWithAnd(
                  <span key={`dept-${dept}`} className="filter-pill">
                    Department: {dept}
                    <button
                      type="button"
                      className="filter-pill-remove"
                      onClick={() => applyFilters({
                        ...filters,
                        departmentFilters: filters.departmentFilters.filter((d) => d !== dept),
                      })}
                      aria-label={`Remove ${dept} department filter`}
                    >
                      ✕
                    </button>
                  </span>
                );
              });

              return pills;
            })()}
          </div>
        </div>
      )}

      {loading && <div className="loading-state">Loading activity log...</div>}
      {!loading && error && <div className="error-state">{error}</div>}

      {!loading && !error && totalRecords === 0 && (
        <div className="empty-state">
          <p className="empty-icon">📭</p>
          <p className="empty-title">No activity found</p>
          <p className="empty-subtitle">Try adjusting your filters or search terms</p>
        </div>
      )}

      {!loading && !error && totalRecords > 0 && (
        <>
          <div className="work-logs-results-info">
            Showing {activities.length} of {totalRecords} records
            {totalRecords > 0 && (
              <span> — Export includes all {totalRecords} filtered records</span>
            )}
          </div>

          <div className="activity-log-export-toolbar">
            <ExportButton
              onExportExcel={() => handleExport('excel')}
              excelOnly
              title="Export activity log"
              disabled={isExporting}
            />
            <button
              type="button"
              className="activity-log-export-config-btn"
              onClick={() => setShowExportColumnsConfig(true)}
              title="Configure export columns"
              disabled={isExporting}
            >
              ⚙️
            </button>
          </div>

          <div className="work-logs-table-wrapper">
            {activities.length === 0 ? (
              <div className="empty-state" style={{ marginBottom: '1rem' }}>
                <p className="empty-title">No records on this page</p>
                <p className="empty-subtitle">Try going back to the first page</p>
              </div>
            ) : (
              <table className="work-logs-table activity-log-table">
                <thead>
                  <tr>
                    <th
                      className="col-date"
                      onClick={() => handleSortChange('timestamp')}
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                      title="Click to sort by date"
                    >
                      Date/Time{getSortIndicator('timestamp')}
                    </th>
                    <th
                      className="col-type"
                      onClick={() => handleSortChange('activity_type')}
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                      title="Click to sort by type"
                    >
                      Type{getSortIndicator('activity_type')}
                    </th>
                    <th
                      className="col-title"
                      onClick={() => handleSortChange('description')}
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                      title="Click to sort by activity"
                    >
                      Activity{getSortIndicator('description')}
                    </th>
                    <th
                      className="col-device"
                      onClick={() => handleSortChange('device_name')}
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                      title="Click to sort by device"
                    >
                      Device{getSortIndicator('device_name')}
                    </th>
                    <th
                      className="col-department"
                      onClick={() => handleSortChange('department')}
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                      title="Click to sort by department"
                    >
                      Department{getSortIndicator('department')}
                    </th>
                    <th
                      className="col-technician"
                      onClick={() => handleSortChange('actor_name')}
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                      title="Click to sort by who performed the action"
                    >
                      Performed By{getSortIndicator('actor_name')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {activities.map((act, idx) => {
                    const isComplaint = act.activity_type?.startsWith('complaint_');
                    const isMaintenance = act.activity_type === 'maintenance_logged';
                    const deviceIdentifier = act.device_identifier || (isComplaint ? null : act.entity_id);
                    const canOpenComplaint = Boolean(act.complaint_id);
                    const canOpenDevice = Boolean(act.device_id) && typeof onNavigateToDeviceView === 'function';
                    const canOpenMaintenance = isMaintenance && (
                      Boolean(act.maintenance_log_id)
                      || (Boolean(deviceIdentifier) && typeof onNavigateToMaintenanceHistory === 'function')
                    );
                    const rowClickable = (isComplaint && canOpenComplaint) || canOpenMaintenance;
                    const { label, rest } = getActivityDescriptionParts(act);
                    const { date, time } = formatActivityDateTime(act.timestamp);
                    const rowActionLabel = isComplaint && canOpenComplaint
                      ? 'View complaint'
                      : canOpenMaintenance
                        ? 'View maintenance log'
                        : undefined;

                    const handleRowClick = () => {
                      if (isComplaint && canOpenComplaint) openComplaint(act);
                      else if (canOpenMaintenance) openMaintenance(act);
                    };

                    const handleRowKeyDown = (e) => {
                      if (!rowClickable) return;
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleRowClick();
                      }
                    };

                    return (
                      <tr
                        key={`${act.activity_type}-${act.entity_id}-${act.timestamp}-${idx}`}
                        className={`work-log-row${isComplaint ? ' is-complaint' : isMaintenance ? ' is-maintenance' : ''}${rowClickable ? ' is-clickable' : ' is-static'}`}
                        onClick={rowClickable ? handleRowClick : undefined}
                        onKeyDown={rowClickable ? handleRowKeyDown : undefined}
                        tabIndex={rowClickable ? 0 : undefined}
                        role={rowClickable ? 'button' : undefined}
                        title={rowActionLabel}
                        aria-label={rowActionLabel}
                      >
                        <td className="col-date">
                          <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <div style={{ fontWeight: 500, fontSize: '0.9rem', color: '#2c3e50' }}>{date}</div>
                            <div style={{ fontWeight: 500, fontSize: '0.8rem', color: '#6c757d' }}>{time}</div>
                          </div>
                        </td>
                        <td className="col-type">
                          <div className={isComplaint ? 'type-stacked' : 'type-simple'}>
                            <span className="type-text">{getActivityTypeLabel(act.activity_type)}</span>
                            {isComplaint && (
                              <SeverityBadge severity={act.severity} size="small" />
                            )}
                            {isMaintenance && act.maintenance_type && (
                              <span className="maintenance-type-badge" data-type={act.maintenance_type?.toLowerCase()}>
                                {getMaintenanceTypeLabel(act.maintenance_type)}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="col-title">
                          <div className="title-content">
                            {isComplaint && act.entity_id && (
                              <ComplaintIDBadge
                                complaintId={act.entity_id}
                                size="small"
                                clickable={canOpenComplaint}
                                onClick={canOpenComplaint ? (e) => { e.stopPropagation(); openComplaint(act); } : undefined}
                              />
                            )}
                            <span className="title-text">
                              {label ? `${label}${rest ? `: ${rest}` : ''}` : rest}
                            </span>
                          </div>
                        </td>
                        <td className="col-device">
                          {deviceIdentifier ? (
                            <div className="device-info">
                              <span className="device-name">{act.device_name || deviceIdentifier}</span>
                              <DeviceIDBadge
                                deviceId={deviceIdentifier}
                                size="small"
                                clickable={canOpenDevice}
                                onClick={canOpenDevice ? (e) => { e.stopPropagation(); openDevice(act); } : undefined}
                              />
                            </div>
                          ) : (
                            '—'
                          )}
                        </td>
                        <td className="col-department">
                          {act.department_name ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                              <span style={{ fontSize: '1rem' }}>{act.department_icon || '🏢'}</span>
                              <span>{act.department_name}</span>
                            </div>
                          ) : (
                            '—'
                          )}
                        </td>
                        <td className="col-technician">{act.actor_name || 'Unknown'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            perPage={perPage}
            onPerPageChange={(value) => {
              saveUiPreferencePatch({ list_page_size: value });
              setPerPage(value);
              setCurrentPage(1);
            }}
            totalItems={totalRecords}
            itemLabel="records"
          />
        </>
      )}

      {showAdvancedPanel && ReactDOM.createPortal(
        <div
          className="sidebar-drawer-overlay activity-log-filters-drawer-overlay"
          onClick={() => setShowAdvancedPanel(false)}
        >
          <div
            className="sidebar-drawer activity-log-filters-drawer"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="drawer-title">
              <span>🔧 Advanced Filters</span>
              <button
                type="button"
                className="drawer-close-btn"
                onClick={() => setShowAdvancedPanel(false)}
                title="Close filters"
              >
                ✕
              </button>
            </div>

            <div className="activity-log-filter-banner">
              <span className="activity-log-filter-banner__icon" aria-hidden="true">⚠️</span>
              <div>
                <strong>{ACTIVITY_LOG_FILTER_HINTS.bannerLead}</strong>
                {' '}
                {ACTIVITY_LOG_FILTER_HINTS.bannerBody}
              </div>
            </div>

            <div className="drawer-filter-content">
              <div className="filter-group">
                <div className="filter-group-title" onClick={() => toggleSection('dateRange')}>
                  <span className={`group-arrow ${expandedSections.dateRange ? 'open' : ''}`}>▼</span>
                  <span>📅 Date Range</span>
                </div>
                {expandedSections.dateRange && (
                  <div className="filter-group-content activity-log-date-range">
                    <div className="filter-item-wrapper">
                      <div className="filter-label-text">From</div>
                      <input
                        type="date"
                        className="filter-select"
                        value={drawerFilters.dateFrom}
                        onChange={(e) => setDrawerFilters((f) => ({
                          ...f,
                          dateFrom: e.target.value,
                          datePreset: '',
                        }))}
                      />
                    </div>
                    <div className="filter-item-wrapper">
                      <div className="filter-label-text">To</div>
                      <input
                        type="date"
                        className="filter-select"
                        value={drawerFilters.dateTo}
                        onChange={(e) => setDrawerFilters((f) => ({
                          ...f,
                          dateTo: e.target.value,
                          datePreset: '',
                        }))}
                      />
                    </div>
                  </div>
                )}
              </div>

              {locationScoped && availableDepartments.length > 0 && (
                <div className="filter-group">
                  <div className="filter-group-title" onClick={() => toggleSection('department')}>
                    <span className={`group-arrow ${expandedSections.department ? 'open' : ''}`}>▼</span>
                    <span>🏢 Department</span>
                  </div>
                  {expandedSections.department && (
                    <div className="filter-group-content">
                      <div className="filter-item-wrapper full-width">
                        <select
                          className="filter-select"
                          multiple
                          value={drawerFilters.departmentFilters}
                          onChange={(e) => {
                            const selected = Array.from(e.target.selectedOptions, (option) => option.value);
                            setDrawerFilters((f) => ({ ...f, departmentFilters: selected }));
                          }}
                          style={{ minHeight: '80px' }}
                          title="Hold Ctrl/Cmd to select multiple departments"
                        >
                          {availableDepartments
                            .slice()
                            .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                            .map((dept) => (
                              <option key={dept.id} value={dept.name}>
                                {dept.icon || '🏢'} {dept.name}
                              </option>
                            ))}
                        </select>
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="filter-group">
                <div className="filter-group-title" onClick={() => toggleSection('activityTypes')}>
                  <span className={`group-arrow ${expandedSections.activityTypes ? 'open' : ''}`}>▼</span>
                  <span>📋 Activity Types</span>
                  {drawerHasMaintenanceFilters && (
                    <FilterTitleHint>{ACTIVITY_LOG_FILTER_HINTS.activityTypeDisabledMaintenance}</FilterTitleHint>
                  )}
                  {drawerHasComplaintFilters && (
                    <FilterTitleHint>{ACTIVITY_LOG_FILTER_HINTS.activityTypeDisabledComplaint}</FilterTitleHint>
                  )}
                </div>
                {expandedSections.activityTypes && (
                  <div className="filter-group-content">
                    <div className="filter-item-wrapper full-width activity-log-checkbox-list">
                      {activityTypeOptions.map((opt) => {
                        const disabled = isActivityTypeOptionDisabled(opt.id, drawerFilters);
                        return (
                          <label
                            key={opt.id}
                            className={`filter-checkbox-label${disabled ? ' is-disabled' : ''}`}
                          >
                            <input
                              type="checkbox"
                              checked={drawerFilters.activityTypeFilters.includes(opt.id)}
                              disabled={disabled}
                              onChange={(e) => {
                                setDrawerFilters((f) => {
                                  const activityTypeFilters = e.target.checked
                                    ? [...f.activityTypeFilters, opt.id]
                                    : f.activityTypeFilters.filter((id) => id !== opt.id);
                                  const next = { ...f, activityTypeFilters };
                                  if (isDeviceOnlyActivitySelection(activityTypeFilters)) {
                                    next.maintenanceTypeFilters = [];
                                    next.complaintStatusFilters = [];
                                    next.severityFilters = [];
                                  }
                                  return next;
                                });
                              }}
                            />
                            <span>{opt.label}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              <div className="filter-group" style={{ opacity: maintenanceTypeDisabled ? 0.5 : 1 }}>
                <div className="filter-group-title" onClick={() => toggleSection('maintenanceType')}>
                  <span className={`group-arrow ${expandedSections.maintenanceType ? 'open' : ''}`}>▼</span>
                  <span>🔧 Maintenance Type</span>
                  {maintenanceTypeDisabled && (
                    <FilterTitleHint>
                      {drawerDeviceOnly
                        ? ACTIVITY_LOG_FILTER_HINTS.exclusiveGroupDisabledDevice
                        : ACTIVITY_LOG_FILTER_HINTS.maintenanceTypeDisabled}
                    </FilterTitleHint>
                  )}
                </div>
                {expandedSections.maintenanceType && (
                  <div className="filter-group-content">
                    <div className="filter-item-wrapper full-width">
                      <select
                        className="filter-select"
                        multiple
                        value={drawerFilters.maintenanceTypeFilters}
                        disabled={maintenanceTypeDisabled}
                        onChange={(e) => {
                          const selected = Array.from(e.target.selectedOptions, (option) => option.value);
                          setDrawerFilters((f) => {
                            const next = { ...f, maintenanceTypeFilters: selected };
                            if (selected.length > 0) {
                              next.complaintStatusFilters = [];
                              next.severityFilters = [];
                              next.activityTypeFilters = compatibleActivityTypeFilters(
                                next,
                                f.activityTypeFilters,
                              );
                            }
                            return next;
                          });
                        }}
                        style={{ minHeight: '100px' }}
                        title="Hold Ctrl/Cmd to select multiple types"
                      >
                        {MAINTENANCE_TYPE_OPTIONS.map((opt) => (
                          <option key={opt.id} value={opt.id}>{opt.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}
              </div>

              <div className="filter-group" style={{ opacity: complaintFiltersDisabled ? 0.5 : 1 }}>
                <div className="filter-group-title" onClick={() => toggleSection('complaintFilters')}>
                  <span className={`group-arrow ${expandedSections.complaintFilters ? 'open' : ''}`}>▼</span>
                  <span>💬 Complaint Filters</span>
                  {complaintFiltersDisabled && (
                    <FilterTitleHint>
                      {drawerDeviceOnly
                        ? ACTIVITY_LOG_FILTER_HINTS.exclusiveGroupDisabledDevice
                        : ACTIVITY_LOG_FILTER_HINTS.complaintFiltersDisabled}
                    </FilterTitleHint>
                  )}
                </div>
                {expandedSections.complaintFilters && (
                  <div className="filter-group-content complaint-filters-two-col">
                    <div className="filter-item-wrapper">
                      <div className="filter-label-text">Status</div>
                      <select
                        className="filter-select"
                        multiple
                        value={drawerFilters.complaintStatusFilters || []}
                        disabled={complaintFiltersDisabled}
                        onChange={(e) => {
                          const selected = Array.from(e.target.selectedOptions, (option) => option.value);
                          setDrawerFilters((f) => {
                            const next = { ...f, complaintStatusFilters: selected };
                            if (selected.length > 0 || next.severityFilters.length > 0) {
                              next.maintenanceTypeFilters = [];
                              next.activityTypeFilters = compatibleActivityTypeFilters(
                                next,
                                f.activityTypeFilters,
                              );
                            }
                            return next;
                          });
                        }}
                        style={{ minHeight: '80px', width: '100%' }}
                        title="Hold Ctrl/Cmd to select multiple statuses"
                      >
                        {COMPLAINT_STATUS_OPTIONS.map((opt) => (
                          <option key={opt.id} value={opt.id}>{opt.label}</option>
                        ))}
                      </select>
                    </div>
                    <div className="filter-item-wrapper">
                      <div className="filter-label-text">Severity</div>
                      <select
                        className="filter-select"
                        multiple
                        value={drawerFilters.severityFilters}
                        disabled={complaintFiltersDisabled}
                        onChange={(e) => {
                          const selected = Array.from(e.target.selectedOptions, (option) => option.value);
                          setDrawerFilters((f) => {
                            const next = { ...f, severityFilters: selected };
                            if (selected.length > 0 || (next.complaintStatusFilters || []).length > 0) {
                              next.maintenanceTypeFilters = [];
                              next.activityTypeFilters = compatibleActivityTypeFilters(
                                next,
                                f.activityTypeFilters,
                              );
                            }
                            return next;
                          });
                        }}
                        style={{ minHeight: '80px', width: '100%' }}
                        title="Hold Ctrl/Cmd to select multiple severities"
                      >
                        {SEVERITY_OPTIONS.map((opt) => (
                          <option key={opt.id} value={opt.id}>{opt.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="drawer-buttons">
              <button
                type="button"
                className="cancel-btn"
                onClick={() => setShowAdvancedPanel(false)}
                title="Close filters without applying changes"
              >
                Close
              </button>
              <button
                type="button"
                className="reset-btn"
                onClick={() => setDrawerFilters(createInitialActivityLogFilters())}
              >
                Reset
              </button>
              <button type="button" className="apply-btn" onClick={applyDrawerFilters}>
                Apply
              </button>
            </div>
          </div>
        </div>,
        document.body,
      )}

      {selectedComplaintForModal && (
        <ComplaintView
          complaintId={selectedComplaintForModal.id}
          token={token}
          onClose={() => setSelectedComplaintForModal(null)}
          onUpdate={() => {}}
          isFullPage={false}
          user={user}
        />
      )}

      {selectedMaintenanceForModal && (
        <MaintenanceLogView
          maintenanceLogId={selectedMaintenanceForModal.id}
          token={token}
          onClose={() => setSelectedMaintenanceForModal(null)}
          onNavigateToDeviceView={onNavigateToDeviceView}
          onNavigateToMaintenanceHistory={onNavigateToMaintenanceHistory}
        />
      )}

      {showExportColumnsConfig && (
        <ExportColumnConfigurationModal
          isOpen={showExportColumnsConfig}
          columns={exportColumns}
          onColumnsChange={setExportColumns}
          onClose={() => setShowExportColumnsConfig(false)}
          title="Activity Log Export Columns"
        />
      )}
    </div>
  );
};

export default ActivityLogPage;
